import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  AppBar,
  Toolbar,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Grid,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  HourglassEmpty as PendingIcon,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';

// Colors
const colors = {
  primaryDark: '#0D1B2A',
  primaryMid: '#1B263B',
  accent: '#FF6B35',
  teal: '#00B4D8',
  tealLight: '#48CAE4',
  white: '#FFFFFF',
  midGray: '#778DA9',
  success: '#10B981',
  warning: '#F59E0B',
  error: '#EF4444',
};

interface Payment {
  id: string;
  invoiceId: string;
  clientName: string;
  clientEmail: string;
  clientCompany: string | null;
  amountUsd: number;
  description: string | null;
  railPreference: string;
  status: string;
  txHash: string | null;
  referenceNumber: string | null;
  proofFilePath: string | null;
  proofNotes: string | null;
  proofSubmittedAt: string | null;
  receivedAt: string | null;
  reconciledAt: string | null;
  reconciledBy: string | null;
  internalNotes: string | null;
  createdAt: string;
  updatedAt: string;
}

interface Stats {
  byStatus: Record<string, number>;
  totalReceived: number;
  pendingAmount: number;
  thisMonthReceived: number;
}

const statusColors: Record<string, string> = {
  draft: colors.midGray,
  pending: colors.warning,
  proof_submitted: colors.teal,
  received: colors.success,
  reconciled: colors.success,
  refunded: colors.error,
};

const PaymentsAdminPage: React.FC = () => {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [authError, setAuthError] = useState(false);

  // Auth state
  const [credentials, setCredentials] = useState({ user: '', pass: '' });
  const [authHeader, setAuthHeader] = useState<string | null>(null);

  // Filter state
  const [statusFilter, setStatusFilter] = useState('all');

  // Edit dialog state
  const [editDialog, setEditDialog] = useState<{
    open: boolean;
    payment: Payment | null;
    newStatus: string;
    notes: string;
  }>({
    open: false,
    payment: null,
    newStatus: '',
    notes: '',
  });

  // Detail dialog state
  const [detailDialog, setDetailDialog] = useState<{
    open: boolean;
    payment: Payment | null;
  }>({
    open: false,
    payment: null,
  });

  const fetchPayments = useCallback(async (auth: string) => {
    try {
      const res = await fetch(`/api/payments/admin/list?status=${statusFilter}`, {
        headers: { Authorization: auth },
      });

      if (res.status === 401 || res.status === 403) {
        setAuthError(true);
        setAuthHeader(null);
        return;
      }

      const data = await res.json();
      if (data.success) {
        setPayments(data.data.payments);
      } else {
        setError(data.error);
      }
    } catch (err) {
      setError('Failed to load payments');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  const fetchStats = useCallback(async (auth: string) => {
    try {
      const res = await fetch('/api/payments/admin/stats', {
        headers: { Authorization: auth },
      });
      const data = await res.json();
      if (data.success) {
        setStats(data.data);
      }
    } catch (err) {
      console.error('Failed to load stats');
    }
  }, []);

  useEffect(() => {
    if (authHeader) {
      fetchPayments(authHeader);
      fetchStats(authHeader);
    }
  }, [authHeader, statusFilter, fetchPayments, fetchStats]);

  const handleLogin = () => {
    const auth = `Basic ${btoa(`${credentials.user}:${credentials.pass}`)}`;
    setAuthHeader(auth);
    setAuthError(false);
    setLoading(true);
  };

  const handleStatusUpdate = async () => {
    if (!editDialog.payment || !authHeader) return;

    try {
      const res = await fetch('/api/payments/admin/status', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: authHeader,
        },
        body: JSON.stringify({
          invoiceId: editDialog.payment.invoiceId,
          status: editDialog.newStatus,
          notes: editDialog.notes,
        }),
      });

      const data = await res.json();
      if (data.success) {
        setEditDialog({ open: false, payment: null, newStatus: '', notes: '' });
        fetchPayments(authHeader);
        fetchStats(authHeader);
      } else {
        alert(data.error || 'Failed to update status');
      }
    } catch (err) {
      alert('Network error');
    }
  };

  const handleExport = async () => {
    if (!authHeader) return;

    try {
      const res = await fetch(`/api/payments/admin/export?status=${statusFilter}`, {
        headers: { Authorization: authHeader },
      });

      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `redex-payments-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      alert('Export failed');
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  // Login screen
  if (!authHeader || authError) {
    return (
      <Box sx={{ 
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: colors.primaryDark,
      }}>
        <Paper sx={{ p: 4, maxWidth: 400, width: '100%', background: colors.primaryMid }}>
          <Typography variant="h5" sx={{ color: colors.white, mb: 3, textAlign: 'center' }}>
            Payments Admin Login
          </Typography>
          {authError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Invalid credentials
            </Alert>
          )}
          <TextField
            fullWidth
            label="Username"
            value={credentials.user}
            onChange={(e) => setCredentials(prev => ({ ...prev, user: e.target.value }))}
            sx={{ mb: 2, ...textFieldStyle }}
          />
          <TextField
            fullWidth
            type="password"
            label="Password"
            value={credentials.pass}
            onChange={(e) => setCredentials(prev => ({ ...prev, pass: e.target.value }))}
            sx={{ mb: 3, ...textFieldStyle }}
            onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
          />
          <Button
            fullWidth
            variant="contained"
            onClick={handleLogin}
            sx={{
              background: colors.teal,
              '&:hover': { background: colors.tealLight },
            }}
          >
            Login
          </Button>
        </Paper>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: colors.primaryDark 
      }}>
        <CircularProgress sx={{ color: colors.teal }} />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: colors.primaryDark,
    }}>
      {/* Header */}
      <AppBar position="static" elevation={0} sx={{ background: colors.primaryMid }}>
        <Container maxWidth="xl">
          <Toolbar sx={{ justifyContent: 'space-between' }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography variant="h6" sx={{ color: colors.white, fontWeight: 700 }}>
                Payments Admin
              </Typography>
              <Chip label="Internal Use Only" size="small" sx={{ background: colors.warning, color: '#000' }} />
            </Stack>
            <Stack direction="row" spacing={2}>
              <Button
                component="a"
                href="/landing.html"
                startIcon={<ArrowBackIcon />}
                sx={{ color: colors.midGray }}
              >
                Back to Site
              </Button>
              <Button
                onClick={() => setAuthHeader(null)}
                sx={{ color: colors.error }}
              >
                Logout
              </Button>
            </Stack>
          </Toolbar>
        </Container>
      </AppBar>

      <Container maxWidth="xl" sx={{ py: 4 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Stats Cards */}
        {stats && (
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card sx={{ background: colors.primaryMid }}>
                <CardContent>
                  <Typography variant="caption" sx={{ color: colors.midGray }}>
                    Total Received
                  </Typography>
                  <Typography variant="h4" sx={{ color: colors.success, fontWeight: 700 }}>
                    {formatCurrency(stats.totalReceived)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card sx={{ background: colors.primaryMid }}>
                <CardContent>
                  <Typography variant="caption" sx={{ color: colors.midGray }}>
                    This Month
                  </Typography>
                  <Typography variant="h4" sx={{ color: colors.teal, fontWeight: 700 }}>
                    {formatCurrency(stats.thisMonthReceived)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card sx={{ background: colors.primaryMid }}>
                <CardContent>
                  <Typography variant="caption" sx={{ color: colors.midGray }}>
                    Pending
                  </Typography>
                  <Typography variant="h4" sx={{ color: colors.warning, fontWeight: 700 }}>
                    {formatCurrency(stats.pendingAmount)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card sx={{ background: colors.primaryMid }}>
                <CardContent>
                  <Typography variant="caption" sx={{ color: colors.midGray }}>
                    Proof Submitted
                  </Typography>
                  <Typography variant="h4" sx={{ color: colors.teal, fontWeight: 700 }}>
                    {stats.byStatus.proof_submitted || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Actions Bar */}
        <Paper sx={{ p: 2, mb: 3, background: colors.primaryMid }}>
          <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
            <Stack direction="row" spacing={2} alignItems="center">
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel sx={{ color: colors.midGray }}>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                  sx={{
                    color: colors.white,
                    '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.2)' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: colors.teal },
                  }}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="proof_submitted">Proof Submitted</MenuItem>
                  <MenuItem value="received">Received</MenuItem>
                  <MenuItem value="reconciled">Reconciled</MenuItem>
                  <MenuItem value="refunded">Refunded</MenuItem>
                </Select>
              </FormControl>
              <Button
                startIcon={<RefreshIcon />}
                onClick={() => fetchPayments(authHeader)}
                sx={{ color: colors.teal }}
              >
                Refresh
              </Button>
            </Stack>
            <Button
              startIcon={<DownloadIcon />}
              variant="outlined"
              onClick={handleExport}
              sx={{ borderColor: colors.teal, color: colors.teal }}
            >
              Export CSV
            </Button>
          </Stack>
        </Paper>

        {/* Payments Table */}
        <TableContainer component={Paper} sx={{ background: colors.primaryMid }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: colors.midGray, fontWeight: 600 }}>Invoice ID</TableCell>
                <TableCell sx={{ color: colors.midGray, fontWeight: 600 }}>Client</TableCell>
                <TableCell sx={{ color: colors.midGray, fontWeight: 600 }}>Amount</TableCell>
                <TableCell sx={{ color: colors.midGray, fontWeight: 600 }}>Rail</TableCell>
                <TableCell sx={{ color: colors.midGray, fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ color: colors.midGray, fontWeight: 600 }}>Created</TableCell>
                <TableCell sx={{ color: colors.midGray, fontWeight: 600 }}>Proof</TableCell>
                <TableCell sx={{ color: colors.midGray, fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {payments.map((payment) => (
                <TableRow key={payment.id} hover>
                  <TableCell>
                    <Typography
                      sx={{ 
                        color: colors.accent, 
                        fontFamily: 'monospace',
                        fontSize: '0.85rem',
                        fontWeight: 600,
                      }}
                    >
                      {payment.invoiceId}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ color: colors.white }}>{payment.clientName}</Typography>
                    <Typography variant="caption" sx={{ color: colors.midGray }}>
                      {payment.clientEmail}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ color: colors.white, fontWeight: 600 }}>
                      {formatCurrency(payment.amountUsd)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={payment.railPreference}
                      size="small"
                      sx={{
                        background: payment.railPreference === 'USDC' 
                          ? 'rgba(0, 180, 216, 0.2)' 
                          : 'rgba(255, 255, 255, 0.1)',
                        color: payment.railPreference === 'USDC' ? colors.teal : colors.white,
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={payment.status.replace('_', ' ')}
                      size="small"
                      sx={{
                        background: `${statusColors[payment.status]}20`,
                        color: statusColors[payment.status],
                        fontWeight: 600,
                        textTransform: 'capitalize',
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ color: colors.midGray }}>
                    {formatDate(payment.createdAt)}
                  </TableCell>
                  <TableCell>
                    {payment.txHash && (
                      <Tooltip title={payment.txHash}>
                        <Typography
                          sx={{
                            color: colors.teal,
                            fontFamily: 'monospace',
                            fontSize: '0.75rem',
                            maxWidth: 100,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {payment.txHash.slice(0, 10)}...
                        </Typography>
                      </Tooltip>
                    )}
                    {payment.referenceNumber && (
                      <Typography sx={{ color: colors.midGray, fontSize: '0.85rem' }}>
                        Ref: {payment.referenceNumber}
                      </Typography>
                    )}
                    {payment.proofFilePath && (
                      <Chip label="File" size="small" sx={{ background: colors.success, fontSize: '0.7rem' }} />
                    )}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => setDetailDialog({ open: true, payment })}
                          sx={{ color: colors.teal }}
                        >
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Update Status">
                        <IconButton
                          size="small"
                          onClick={() => setEditDialog({
                            open: true,
                            payment,
                            newStatus: payment.status,
                            notes: payment.internalNotes || '',
                          })}
                          sx={{ color: colors.accent }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
              {payments.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} sx={{ textAlign: 'center', py: 4 }}>
                    <Typography sx={{ color: colors.midGray }}>
                      No payments found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Container>

      {/* Edit Status Dialog */}
      <Dialog
        open={editDialog.open}
        onClose={() => setEditDialog({ open: false, payment: null, newStatus: '', notes: '' })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Update Payment Status</DialogTitle>
        <DialogContent>
          {editDialog.payment && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>
                Invoice: <strong>{editDialog.payment.invoiceId}</strong>
              </Typography>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>New Status</InputLabel>
                <Select
                  value={editDialog.newStatus}
                  label="New Status"
                  onChange={(e) => setEditDialog(prev => ({ ...prev, newStatus: e.target.value }))}
                >
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="proof_submitted">Proof Submitted</MenuItem>
                  <MenuItem value="received">Received</MenuItem>
                  <MenuItem value="reconciled">Reconciled</MenuItem>
                  <MenuItem value="refunded">Refunded</MenuItem>
                </Select>
              </FormControl>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Internal Notes"
                value={editDialog.notes}
                onChange={(e) => setEditDialog(prev => ({ ...prev, notes: e.target.value }))}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog({ open: false, payment: null, newStatus: '', notes: '' })}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleStatusUpdate} sx={{ background: colors.teal }}>
            Update Status
          </Button>
        </DialogActions>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog
        open={detailDialog.open}
        onClose={() => setDetailDialog({ open: false, payment: null })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Payment Details</DialogTitle>
        <DialogContent>
          {detailDialog.payment && (
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="caption" color="textSecondary">Invoice ID</Typography>
                  <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                    {detailDialog.payment.invoiceId}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="caption" color="textSecondary">Amount</Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {formatCurrency(detailDialog.payment.amountUsd)}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="caption" color="textSecondary">Client</Typography>
                  <Typography variant="body1">{detailDialog.payment.clientName}</Typography>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="caption" color="textSecondary">Email</Typography>
                  <Typography variant="body1">{detailDialog.payment.clientEmail}</Typography>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="caption" color="textSecondary">Company</Typography>
                  <Typography variant="body1">{detailDialog.payment.clientCompany || '—'}</Typography>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="caption" color="textSecondary">Rail</Typography>
                  <Typography variant="body1">{detailDialog.payment.railPreference}</Typography>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="caption" color="textSecondary">Description</Typography>
                  <Typography variant="body1">{detailDialog.payment.description || '—'}</Typography>
                </Grid>
                {detailDialog.payment.txHash && (
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="caption" color="textSecondary">TX Hash</Typography>
                    <Typography variant="body1" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                      {detailDialog.payment.txHash}
                    </Typography>
                  </Grid>
                )}
                {detailDialog.payment.referenceNumber && (
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="caption" color="textSecondary">Reference Number</Typography>
                    <Typography variant="body1">{detailDialog.payment.referenceNumber}</Typography>
                  </Grid>
                )}
                {detailDialog.payment.proofNotes && (
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="caption" color="textSecondary">Proof Notes</Typography>
                    <Typography variant="body1">{detailDialog.payment.proofNotes}</Typography>
                  </Grid>
                )}
                {detailDialog.payment.internalNotes && (
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="caption" color="textSecondary">Internal Notes</Typography>
                    <Typography variant="body1">{detailDialog.payment.internalNotes}</Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialog({ open: false, payment: null })}>
            Close
          </Button>
          {detailDialog.payment && (
            <Button
              component={Link}
              to={`/payments/receipt/${detailDialog.payment.invoiceId}`}
              target="_blank"
              variant="outlined"
            >
              View Receipt Page
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const textFieldStyle = {
  '& .MuiOutlinedInput-root': {
    color: colors.white,
    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
    '&:hover fieldset': { borderColor: colors.teal },
    '&.Mui-focused fieldset': { borderColor: colors.teal },
  },
  '& .MuiInputLabel-root': {
    color: colors.midGray,
    '&.Mui-focused': { color: colors.teal },
  },
};

export default PaymentsAdminPage;
